import {
  AlignmentType,
  BorderStyle,
  ImageRun,
  LineRuleType,
  Paragraph,
  Table,
  TabStopType,
  TextRun,
  type FileChild,
} from "docx";
import type { CheerioAPI } from "cheerio";
import type { AnyNode, Element } from "domhandler";
import { prependElementBookmark } from "./bookmarks.js";
import {
  atLeastLineTwips,
  BODY_FONT_HALF_POINTS,
  BODY_LINE_EXACT_TWIPS,
  BODY_LINE_HEIGHT,
  BLOCKQUOTE_MARGIN_PX,
  BLOCKQUOTE_INDENT_PX,
  BLOCKQUOTE_UA_SIDE_MARGIN_PX,
  DEFAULT_LINE_HEIGHT,
  LIST_HANGING_TWIPS,
  LIST_LEVEL_LEFT_TWIPS,
  DEFAULT_PARAGRAPH_MARGIN_PX,
  HEADING_FONT_HALF_POINTS,
  HEADING_LEVELS,
  HEADING_MARGIN_EM,
  BODY_TEXT_COLOR,
  LIST_STYLE_REFERENCES,
} from "./constants.js";
import {
  blockLayoutToParagraphProps,
  cssToBlockLayout,
  elementRequestsPageBreakAfter,
  isBlockElement,
  isHiddenElement,
  layoutForNativeShadedBlock,
  layoutFromElement,
  pxPaddingToBorderSpace,
  pxToHalfPoints,
  pxToTwips,
  shadedBlockParagraphSpacing,
  typographyFromBlockElement,
} from "./css.js";
import { makeBorderedTableBlock, makeShadedBlockTable, makeShadedContainerTable, needsShadedTableWrapper, shouldUseBorderedTable } from "./bordered-block.js";
import {
  elementPlainText,
  flexItemElements,
  estimateFlexItemWidthTwips,
  isFlexContainer,
  makeFlexColumnTable,
  makeFlexRowTable,
  parseFlexLayoutFromCss,
} from "./flex.js";
import {
  atLeastLineTwipsForSuperSubInline,
  collectInlineRunsFromNodes,
  getDirectBlockChildren,
  getInlineOrTextNodes,
  hasDirectBlockChild,
} from "./inline.js";
import { convertTable } from "./table.js";
import { convertSvg } from "./svg.js";
import type { BlockLayout, DocxBlock, RunTypography, VisitorContext } from "./types.js";
import { DEFAULT_VISITOR_CONTEXT } from "./types.js";
import type { StyleResolver } from "./style-resolver.js";
import { INLINE_STYLE_RESOLVER } from "./style-resolver.js";
import type { InlineFieldOptions } from "./fields.js";

function isElement(node: AnyNode): node is Element {
  return node.type === "tag";
}

function mergeBlockLayouts(base: BlockLayout, overlay: BlockLayout): BlockLayout {
  return {
    alignment: overlay.alignment ?? base.alignment,
    shading: overlay.shading ?? base.shading,
    spacingBefore: overlay.spacingBefore ?? base.spacingBefore,
    spacingAfter: overlay.spacingAfter ?? base.spacingAfter,
    indentLeft: sumIndent(base.indentLeft, overlay.indentLeft),
    indentRight: overlay.indentRight ?? base.indentRight,
    borders: overlay.borders ?? base.borders,
  };
}

function sumIndent(base: number | undefined, overlay: number | undefined): number | undefined {
  const total = (base ?? 0) + (overlay ?? 0);
  return total > 0 ? total : undefined;
}

function sumTwips(...values: Array<number | undefined>): number | undefined {
  const total = values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
  return total > 0 ? total : undefined;
}

function nextElementSibling(element: Element): Element | undefined {
  let next = element.next;
  while (next) {
    if (isElement(next)) return next;
    next = next.next;
  }
  return undefined;
}

function prevElementSibling(element: Element): Element | undefined {
  let prev = element.prev;
  while (prev) {
    if (isElement(prev)) return prev;
    prev = prev.prev;
  }
  return undefined;
}

/** Block containers never inherit shading — each block gets its own paragraph shading. */
function applyDefaultParagraphMargins(
  element: Element,
  layout: BlockLayout,
  ctx: VisitorContext,
): BlockLayout {
  if (element.name.toLowerCase() !== "p") return layout;
  // `<p>` inside blockquote — vertical margin lives on the blockquote container.
  if (ctx.blockquoteDepth > 0) return layout;
  if (ctx.styleResolver.source === "computed") return layout;

  const css = ctx.styleResolver.getCss(element);
  let defaultMargin = pxToTwips(DEFAULT_PARAGRAPH_MARGIN_PX);
  if (css.fontSize) {
    defaultMargin = pxToTwips(css.fontSize / 1.5);
  }
  const result = { ...layout };

  if (css.marginBottom === undefined) {
    result.marginBottom = (result.marginBottom ?? 0) + defaultMargin;
  }
  if (css.marginTop === undefined) {
    // Collapse with previous `<p>` margin-bottom (HTML max-margin behavior).
    const prev = prevElementSibling(element);
    if (!(prev && isParagraphElement(prev))) {
      result.marginTop = (result.marginTop ?? 0) + defaultMargin;
    }
  }

  return {
    ...result,
    spacingBefore: sumTwips(result.paddingTop, result.marginTop),
    spacingAfter: sumTwips(result.paddingBottom, result.marginBottom),
  };
}

function collapseMarginTopAfterBlock(
  element: Element,
  layout: BlockLayout,
  ctx: VisitorContext,
): BlockLayout {
  if (ctx.styleResolver.source === "computed") return layout;
  if (!layout.marginTop) return layout;

  const tag = element.name.toLowerCase();
  if (tag !== "ul" && tag !== "ol" && tag !== "div" && tag !== "section" && tag !== "blockquote") {
    return layout;
  }

  const prev = prevElementSibling(element);
  if (!prev || !isElement(prev)) return layout;
  const prevTag = prev.name.toLowerCase();
  if (prevTag === "p" || /^h[1-6]$/.test(prevTag) || prevTag === "blockquote") {
    return {
      ...layout,
      marginTop: 0,
      spacingBefore: layout.paddingTop,
    };
  }
  return layout;
}

function collapseMarginTopAfterParagraph(
  element: Element,
  layout: BlockLayout,
  ctx: VisitorContext,
): BlockLayout {
  if (ctx.styleResolver.source === "computed") return layout;
  if (element.name.toLowerCase() !== "p") return layout;
  if (!layout.marginTop) return layout;

  const prev = prevElementSibling(element);
  if (!prev || !isElement(prev)) return layout;
  const prevTag = prev.name.toLowerCase();
  if (prevTag === "p" || /^h[1-6]$/.test(prevTag) || prevTag === "blockquote") {
    return {
      ...layout,
      marginTop: 0,
      spacingBefore: layout.paddingTop,
    };
  }
  return layout;
}

function resolveBlockLayout(ctx: VisitorContext, element: Element): BlockLayout {
  const local = collapseMarginTopAfterBlock(
    element,
    collapseMarginTopAfterParagraph(
      element,
      applyDefaultParagraphMargins(
        element,
        layoutFromElement(element, ctx.styleResolver),
        ctx,
      ),
      ctx,
    ),
    ctx,
  );
  const inherited = ctx.inheritedLayout ?? {};
  const layout = {
    ...local,
    alignment: local.alignment ?? inherited.alignment,
    indentLeft: sumIndent(inherited.indentLeft, local.indentLeft),
    indentRight: local.indentRight ?? inherited.indentRight,
    borders: local.borders ?? inherited.borders,
  };
  if (ctx.pageBreakBeforeNext) {
    layout.pageBreakBefore = true;
  }
  // A background on a media-only wrapper (e.g. a figure image inside a styled link or
  // "open in modal" trigger) is web chrome the image sits on — painting it as a filled
  // block adds nothing to a flat document and shows as a bare colored box when the image
  // can't be resolved. Drop the fill so the image (or its alt text) stands on its own.
  if (layout.shading?.fill && isMediaOnlyContainer(element)) {
    layout.shading = undefined;
  }
  return layout;
}

function emptyRun(size = BODY_FONT_HALF_POINTS): TextRun {
  return new TextRun({ text: "", size });
}

/** Pin spacer paragraphs to ~1pt so LO does not add a full default line box before `w:after`. */
const MARGIN_SPACER_LINE_TWIPS = 20;

function marginSpacer(
  afterTwips: number | undefined,
  carry?: BlockLayout,
): Paragraph | undefined {
  if (!afterTwips) return undefined;
  // Inside a blockquote the spacer carries the quote's left border + indent so
  // the vertical rule stays continuous across margin gaps (matches the browser).
  const carryProps =
    carry && (carry.borders?.left || carry.indentLeft)
      ? blockLayoutToParagraphProps({
          indentLeft: carry.indentLeft,
          borders: carry.borders?.left ? { left: carry.borders.left } : undefined,
        })
      : {};
  // Borders only paint along the line box, so a carried bar needs the gap's
  // full height in the EXACT line (not in w:after) to span it.
  const hasBar = Boolean(carry?.borders?.left);
  return new Paragraph({
    ...carryProps,
    spacing: hasBar
      ? {
          after: 0,
          before: 0,
          line: afterTwips,
          lineRule: LineRuleType.EXACT,
        }
      : {
          after: afterTwips,
          before: 0,
          line: MARGIN_SPACER_LINE_TWIPS,
          lineRule: LineRuleType.EXACT,
        },
    // Keep non-bar spacers as truly empty paragraphs so desktop Word does
    // not render an unselectable white overlay block below tables.
    children: hasBar ? [emptyRun()] : [],
  });
}

function isParagraphElement(node: AnyNode): boolean {
  return isElement(node) && node.name.toLowerCase() === "p";
}

function nodesHaveLineBreaks(nodes: AnyNode[]): boolean {
  for (const node of nodes) {
    if (!isElement(node)) continue;
    if (node.name.toLowerCase() === "br") return true;
    if (nodesHaveLineBreaks(node.children ?? [])) return true;
  }
  return false;
}

/** HTML margin collapse with adjacent `<p>` — avoid double-counting in Word spacers. */
function shadedMarginTopSpacer(element: Element | undefined, layout: BlockLayout): Paragraph | undefined {
  if (!layout.marginTop) return undefined;
  const defaultPMargin = pxToTwips(DEFAULT_PARAGRAPH_MARGIN_PX);
  const prev = element ? prevElementSibling(element) : undefined;
  if (prev && isParagraphElement(prev) && layout.marginTop <= defaultPMargin) {
    return undefined;
  }
  return marginSpacer(layout.marginTop);
}

function emitBlockContent(
  runs: ReturnType<typeof collectInlineRunsFromNodes>,
  layout: BlockLayout,
  extra: Record<string, unknown> = {},
  element?: Element,
): DocxBlock[] {
  if (shouldUseBorderedTable(layout)) {
    const blocks: DocxBlock[] = [];
    const topSpacer = marginSpacer(layout.marginTop);
    if (topSpacer) blocks.push(topSpacer);
    blocks.push(makeBorderedTableBlock(runs, layout, extra));
    const bottomSpacer = marginSpacer(layout.marginBottom);
    if (bottomSpacer) blocks.push(bottomSpacer);
    return blocks;
  }

  if (layout.shading?.fill) {
    const blocks: DocxBlock[] = [];
    const topSpacer = shadedMarginTopSpacer(element, layout);
    if (topSpacer) blocks.push(topSpacer);
    blocks.push(
      needsShadedTableWrapper(layout)
        ? makeShadedBlockTable(runs, layoutForNativeShadedBlock(layout))
        : makeParagraph(runs, layoutForNativeShadedBlock(layout), extra),
    );
    return blocks;
  }

  return [makeParagraph(runs, layout, extra)];
}

function makeParagraph(
  children: ReturnType<typeof collectInlineRunsFromNodes>,
  layout: BlockLayout,
  extra: Record<string, unknown> = {},
): Paragraph {
  const isShaded = Boolean(layout.shading?.fill);
  const hasVerticalPad = (layout.paddingTop ?? 0) > 0 || (layout.paddingBottom ?? 0) > 0;
  const padLeft = layout.paddingLeft ?? 0;
  const padRight = layout.paddingRight ?? 0;
  const isListItem = Boolean(extra.numbering);

  // An image in the document flow needs breathing room from adjacent blocks. Web
  // layouts separate figures with margins the computed path faithfully zeroes (flex/grid
  // `gap`, container padding), leaving the flat docx with the image smashed against the
  // next heading/paragraph. Floor an image paragraph's before/after spacing to ~0.5em (a
  // larger real margin still wins). Skipped for flex-card content, which manages its own
  // tight rhythm (it would otherwise inflate).
  const hasImage = children.some((c) => c instanceof ImageRun);
  const spacingLayout =
    hasImage && !isShaded && !isListItem && !extra.flexContent
      ? {
          ...layout,
          spacingBefore: Math.max(layout.spacingBefore ?? 0, pxToTwips(8)),
          spacingAfter: Math.max(layout.spacingAfter ?? 0, pxToTwips(8)),
        }
      : layout;

  const layoutProps = blockLayoutToParagraphProps(
    isShaded
      ? {
          ...layout,
          spacingBefore: undefined,
          spacingAfter: undefined,
          indentLeft: undefined,
          indentRight: padRight > 0 ? padRight : undefined,
        }
      : spacingLayout,
  );

  let paragraphChildren =
    children.length > 0 ? children : [emptyRun()];

  const paragraphExtra: Record<string, unknown> = { ...extra };

  if (isShaded && padLeft > 0) {
    paragraphExtra.tabStops = [{ type: TabStopType.LEFT, position: padLeft }];
    paragraphChildren = [
      new TextRun({
        text: "\t",
        size: layout.shadedTabHalfPoints ?? BODY_FONT_HALF_POINTS,
      }),
      ...paragraphChildren,
    ];
  }

  const shadedSpacing =
    isShaded && hasVerticalPad
      ? { spacing: shadedBlockParagraphSpacing(layout) }
      : {};

  const hasLineBreaks = Boolean(extra.hasLineBreaks);
  const isFlexCompact = Boolean(extra.flexCompact);

  const metricSpacing =
    hasLineBreaks || isFlexCompact
      ? {
          spacing: {
            ...(layoutProps.spacing ?? {}),
            line: BODY_LINE_HEIGHT,
            lineRule: LineRuleType.EXACT,
            before: layoutProps.spacing?.before ?? 0,
            after: layoutProps.spacing?.after ?? 0,
          },
        }
      : {};

  const listItemSpacing = isListItem
    ? {
        spacing: {
          before: 0,
          after: 0,
          line: BODY_LINE_EXACT_TWIPS,
          lineRule: LineRuleType.EXACT,
        },
        contextualSpacing: true,
      }
    : {};

  // EXACT per-font line box for flex-item block children (card lines).
  const exactFontLine = typeof extra.exactLineTwips === "number" ? (extra.exactLineTwips as number) : undefined;
  const exactFontSpacing = exactFontLine
    ? {
        spacing: {
          ...(layoutProps.spacing ?? {}),
          line: exactFontLine,
          lineRule: LineRuleType.EXACT,
          before: layoutProps.spacing?.before ?? 0,
          after: layoutProps.spacing?.after ?? 0,
        },
      }
    : {};

  // CSS line-height → AT_LEAST font×multiplier (AUTO multiplies Word's natural
  // ~1.15em line — too tall; EXACT clips taller inline content like images).
  // Merged WITH the margin spacing — a separate spread would be overwritten.
  const lineHeightMult = layout.lineHeight ?? DEFAULT_LINE_HEIGHT;
  const atLeastLine =
    typeof extra.atLeastLineTwips === "number"
      ? (extra.atLeastLineTwips as number)
      : layout.lineHeight
        ? atLeastLineTwips(BODY_FONT_HALF_POINTS, lineHeightMult)
        : BODY_LINE_EXACT_TWIPS;
  const defaultLineSpacing = {
    spacing: {
      ...(layoutProps.spacing ?? {}),
      line: atLeastLine,
      lineRule: LineRuleType.AT_LEAST,
    },
  };

  return new Paragraph({
    ...layoutProps,
    ...(isShaded && hasVerticalPad
      ? {}
      : hasLineBreaks || isFlexCompact || isListItem || exactFontLine
        ? {}
        : defaultLineSpacing),
    ...(isShaded && hasVerticalPad
      ? shadedSpacing
      : isListItem
        ? listItemSpacing
        : exactFontLine
          ? exactFontSpacing
          : metricSpacing),
    ...(isShaded ? { contextualSpacing: false } : {}),
    ...paragraphExtra,
    children: paragraphChildren,
  });
}

function blockquoteLayout(
  depth: number,
  element: Element,
  styleResolver: StyleResolver,
  parentIndentTwips = 0,
): BlockLayout {
  const css = styleResolver.getCss(element);
  let local = cssToBlockLayout(css);
  const hasExplicitLeftBorder = Boolean(css.borderLeft ?? css.border);

  let layout: BlockLayout;
  if (styleResolver.source === "computed") {
    const inlineStyle = element.attribs?.style ?? "";
    const hasInlineMargin = /\bmargin(?:\s|:|-)/i.test(inlineStyle);
    if (!hasInlineMargin) {
      local = {
        ...local,
        marginTop: undefined,
        marginBottom: undefined,
        marginLeft: undefined,
        marginRight: undefined,
        spacingBefore: local.paddingTop,
        spacingAfter: local.paddingBottom,
        indentLeft: local.paddingLeft,
        indentRight: local.paddingRight,
      };
    } else {
      local = {
        ...local,
        marginLeft: undefined,
        marginRight: undefined,
        indentLeft: local.paddingLeft,
        indentRight: local.paddingRight,
      };
    }
    layout = {
      ...local,
      indentLeft: sumIndent(local.indentLeft, pxToTwips(BLOCKQUOTE_INDENT_PX * depth)),
      marginTop: sumTwips(local.marginTop, pxToTwips(BLOCKQUOTE_MARGIN_PX)),
      marginBottom: sumTwips(local.marginBottom, pxToTwips(BLOCKQUOTE_MARGIN_PX)),
    };
  } else {
    // UA box model: margin 1em 40px unless the inline style says otherwise.
    // Content indent accumulates parent quote indent + margin + border + padding.
    const borderPx = (css.borderLeft ?? css.border)?.widthPx ?? 0;
    const ownOffset =
      (css.marginLeft ?? pxToTwips(BLOCKQUOTE_UA_SIDE_MARGIN_PX)) +
      pxToTwips(borderPx) +
      (css.paddingLeft ?? 0);
    layout = {
      ...local,
      marginLeft: undefined,
      marginRight: undefined,
      marginTop: css.marginTop ?? pxToTwips(BLOCKQUOTE_MARGIN_PX),
      marginBottom: css.marginBottom ?? pxToTwips(BLOCKQUOTE_MARGIN_PX),
      spacingBefore: local.paddingTop,
      spacingAfter: local.paddingBottom,
      indentLeft: sumIndent(parentIndentTwips, ownOffset),
      indentRight: local.paddingRight,
    };
  }

  if (hasExplicitLeftBorder) {
    const border = local.borders?.left ?? {
      color: depth > 1 ? "666666" : "333333",
      size: 24,
      space: pxPaddingToBorderSpace(css.paddingLeft),
    };
    layout.borders = { ...local.borders, left: border };
  }

  return layout;
}

function mediaHeightPx(el: Element): number | undefined {
  const h = el.attribs?.height;
  if (!h) return undefined;
  const n = parseFloat(h);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function maxInlineMediaHeightTwips(nodes: AnyNode[]): number {
  let maxTwips = 0;
  const walk = (node: AnyNode): void => {
    if (!isElement(node)) return;
    const tag = node.name.toLowerCase();
    if (tag === "img" || tag === "canvas") {
      const h = mediaHeightPx(node);
      if (h) maxTwips = Math.max(maxTwips, pxToTwips(h));
    }
    for (const child of node.children ?? []) walk(child);
  };
  for (const node of nodes) walk(node);
  return maxTwips;
}

/** Flush a fresh paragraph on block boundaries; inline backgrounds stay on TextRuns. */
function emitFlowBlocks(
  $: CheerioAPI,
  nodes: AnyNode[],
  blockLayout: BlockLayout,
  inheritedTypography: RunTypography,
  ctx: VisitorContext,
  extra: Record<string, unknown> = {},
  containerElement?: Element,
): DocxBlock[] {
  const blocks: DocxBlock[] = [];
  let pendingInline: AnyNode[] = [];

  const flush = (): void => {
    if (pendingInline.length === 0 && !blockLayout.shading) return;
    const runs = collectInlineRunsFromNodes(
      pendingInline,
      inheritedTypography,
      undefined,
      ctx.styleResolver,
      ctx.defaultSizeHalfPoints,
      ctx.fieldOptions,
    );
    // Pretty-printed HTML adds whitespace text nodes around block children; browsers
    // collapse them, but an empty flush would become an emptyRun paragraph — and in
    // flex cards that picks up EXACT line height (~1.4em), leaving a visible band
    // above/below images (flex-row-images repro).
    if (runs.length === 0 && !blockLayout.shading) {
      pendingInline = [];
      return;
    }
    const hasLineBreaks = nodesHaveLineBreaks(pendingInline);
    // In a flex-item's block children, size each line box to its own font (CSS
    // line-height), not AUTO — halfPt/2 → pt × lh × 20 twips = halfPt × (lh × 10).
    // Rasterized chart images need a taller line box or LibreOffice clips them.
    const lineHeightMult = blockLayout.lineHeight ?? DEFAULT_LINE_HEIGHT;
    const fontSizeHp = inheritedTypography.fontSize ?? ctx.defaultSizeHalfPoints;
    const textLineTwips = atLeastLineTwips(fontSizeHp, lineHeightMult);
    const mediaTwips = ctx.flexBlockContent ? maxInlineMediaHeightTwips(pendingInline) : 0;
    const mountTwips =
      ctx.flexBlockContent && mediaTwips > 0 && containerElement
        ? flexMediaMountHeightTwips(containerElement, ctx)
        : 0;
    const mountSpacerTwips = mountTwips > mediaTwips ? mountTwips - mediaTwips : 0;
    const superSubLineTwips = atLeastLineTwipsForSuperSubInline(
      pendingInline,
      ctx.defaultSizeHalfPoints,
      ctx.styleResolver,
    );
    // EXACT line height crops an inline image: the image sits a full image-height
    // above the baseline, which overflows an EXACT box of that same height, so
    // LibreOffice clips its top. Use EXACT only for pure-text flex card lines; when
    // the line carries a raster image, switch to AT_LEAST the image height so the
    // line grows to fit it instead of cropping.
    const exactLineTwips =
      ctx.flexBlockContent && mediaTwips === 0 ? textLineTwips : undefined;
    blocks.push(
      ...emitBlockContent(
        runs,
        blockLayout,
        {
          ...extra,
          hasLineBreaks,
          exactLineTwips,
          // Flex-card content manages its own tight vertical rhythm — never image-floor it.
          flexContent: ctx.flexBlockContent,
          ...(mediaTwips > 0
            ? { atLeastLineTwips: Math.max(textLineTwips, mediaTwips) }
            : superSubLineTwips
              ? { atLeastLineTwips: Math.max(textLineTwips, superSubLineTwips) }
              : inheritedTypography.fontSize || blockLayout.lineHeight
                ? { atLeastLineTwips: textLineTwips }
                : {}),
        },
        containerElement,
      ),
    );
    // Chart mount divs are taller than the rasterized image — fill the remainder
    // below the image with an EXACT spacer line so the card border matches HTML
    // without putting the image on the baseline of a tall AT_LEAST line box.
    if (mountSpacerTwips > 0) {
      blocks.push(
        makeParagraph([], blockLayout, {
          exactLineTwips: mountSpacerTwips,
          flexContent: ctx.flexBlockContent,
        }),
      );
    }
    pendingInline = [];
  };

  for (const node of nodes) {
    if (isElement(node) && isBlockElement(node, ctx.styleResolver)) {
      flush();
      blocks.push(...visitElement($, node, { ...ctx, inheritedLayout: undefined }));
      continue;
    }
    pendingInline.push(node);
  }

  flush();
  return blocks.length > 0 ? blocks : emitBlockContent([], blockLayout, extra, containerElement);
}

/**
 * A container whose visible content is only media (`<img>`/`<svg>`/`<canvas>`/`<picture>`)
 * with no meaningful text — e.g. a figure image wrapped in a styled link or "open in modal"
 * trigger. Its computed `background-color` is web chrome the image sits on top of; painting
 * it as a filled block adds nothing to a flat document and renders as a bare colored box when
 * the image can't be resolved (the exact "blue box" seen converting real docs pages). Callers
 * drop the container fill in this case.
 */
function isMediaOnlyContainer(element: Element): boolean {
  let hasMedia = false;
  let hasText = false;
  const walk = (nodes: AnyNode[]): void => {
    for (const node of nodes) {
      if (node.type === "text") {
        if ((node.data ?? "").trim()) hasText = true;
      } else if (isElement(node)) {
        const tag = node.name.toLowerCase();
        if (tag === "img" || tag === "svg" || tag === "canvas" || tag === "picture") hasMedia = true;
        walk(node.children ?? []);
      }
    }
  };
  walk(element.children ?? []);
  return hasMedia && !hasText;
}

/** Explicit height (twips) of direct block children — chart mount divs, etc. */
function directBlockChildHeightTwips(item: Element, ctx: VisitorContext): number {
  let max = 0;
  for (const child of getDirectBlockChildren(item, ctx.styleResolver)) {
    const h = ctx.styleResolver.getCss(child).heightTwips;
    if (h) max = Math.max(max, h);
  }
  return max;
}

/** Mount-box height for a flex media line: the element's own height or a direct block child. */
function flexMediaMountHeightTwips(element: Element, ctx: VisitorContext): number {
  const own = ctx.styleResolver.getCss(element).heightTwips ?? 0;
  return Math.max(own, directBlockChildHeightTwips(element, ctx));
}

/** True when the flex item subtree contains a raster `<img>` / `<canvas>`. */
function flexItemHasRasterMedia(item: Element): boolean {
  const walk = (el: Element): boolean => {
    const tag = el.name.toLowerCase();
    if (tag === "img" || tag === "canvas") return true;
    for (const child of el.children ?? []) {
      if (isElement(child) && walk(child)) return true;
    }
    return false;
  };
  return walk(item);
}

/**
 * Tight content height (twips) of a card-style flex item: one EXACT line box per
 * block child (fontSize × 14 = size × 1.4) plus the item's top/bottom padding.
 * Rasterized charts/images can be much taller than a single text line — include them.
 *
 * Prefer explicit `<img>` / `<canvas>` height attributes over ancestor CSS heights
 * (e.g. Highcharts `.highcharts-container`). Mixing wrapper CSS into EXACT row
 * height makes LibreOffice draw a selection frame that overflows neighboring text;
 * Word is more forgiving.
 */
function estimateFlexItemMediaHeightTwips(
  item: Element,
  itemLayout: BlockLayout,
  ctx: VisitorContext,
): number {
  let mediaTwips = 0;
  let cssTwips = 0;
  const walk = (el: Element): void => {
    const tag = el.name.toLowerCase();
    if (tag === "img" || tag === "canvas") {
      const h = mediaHeightPx(el);
      if (h) mediaTwips = Math.max(mediaTwips, pxToTwips(h));
    }
    const css = ctx.styleResolver.getCss(el);
    if (css.heightTwips) cssTwips = Math.max(cssTwips, css.heightTwips);
    for (const child of el.children ?? []) {
      if (isElement(child)) walk(child);
    }
  };
  walk(item);
  const directChildTwips = directBlockChildHeightTwips(item, ctx);
  // Explicit media size wins over deep ancestor CSS (Highcharts wrappers), but a
  // direct block-child height (chart mount div) still bounds the card content box.
  const maxTwips =
    mediaTwips > 0 ? Math.max(mediaTwips, directChildTwips) : cssTwips;
  if (maxTwips === 0) return 0;
  return maxTwips + (itemLayout.paddingTop ?? 0) + (itemLayout.paddingBottom ?? 0);
}

function estimateFlexItemContentHeight(
  item: Element,
  itemLayout: BlockLayout,
  ctx: VisitorContext,
): number {
  let lines = 0;
  for (const child of getDirectBlockChildren(item, ctx.styleResolver)) {
    const fontSize = typographyFromBlockElement(child, ctx.styleResolver).fontSize ?? ctx.defaultSizeHalfPoints;
    lines += fontSize * 14;
  }
  const textHeight = lines + (itemLayout.paddingTop ?? 0) + (itemLayout.paddingBottom ?? 0);
  const mediaHeight = estimateFlexItemMediaHeightTwips(item, itemLayout, ctx);
  return Math.max(textHeight, mediaHeight);
}

function processFlexContainer(
  $: CheerioAPI,
  element: Element,
  ctx: VisitorContext,
): DocxBlock[] {
  const flex = parseFlexLayoutFromCss(ctx.styleResolver.getCss(element));
  if (!flex) return [];

  const containerLayout = resolveBlockLayout(ctx, element);
  const childCtx: VisitorContext = { ...ctx, inheritedLayout: undefined };
  const items = flexItemElements(element, ctx.styleResolver).map((item) => {
    const itemCss = ctx.styleResolver.getCss(item);
    const itemLayout = cssToBlockLayout(itemCss);
    const typography = typographyFromBlockElement(item, ctx.styleResolver);
    const intrinsicWidthTwips =
      flex.direction === "row"
        ? estimateFlexItemWidthTwips(item, itemLayout, ctx.styleResolver)
        : undefined;

    if (hasDirectBlockChild(item, ctx.styleResolver)) {
      // Card-style flex items: tighten stacked lines to CSS line boxes (EXACT per font)
      // and force an exact row height, since LibreOffice sizes rows by natural metrics.
      // Skip EXACT when the item is (or wraps) a raster image — LibreOffice overflows
      // neighboring paragraphs when an EXACT row fights the drawing's intrinsic size
      // (common after chart rasterize leaves a tall Highcharts wrapper around an <img>).
      const itemCtx: VisitorContext = { ...childCtx, flexBlockContent: true };
      const hasMedia = flexItemHasRasterMedia(item);
      const contentHeightTwips = hasMedia
        ? estimateFlexItemMediaHeightTwips(item, itemLayout, ctx)
        : estimateFlexItemContentHeight(item, itemLayout, ctx);
      // Render the item's CHILDREN, not the item element: the flex cell already
      // paints the item's border / background / padding (see makeFlexRowTable), so
      // re-rendering the item div would wrap the content in a second bordered
      // container table — a doubled box around each card.
      const contentLayout: BlockLayout = { alignment: itemLayout.alignment };
      return {
        layout: itemLayout,
        blocks: emitFlowBlocks($, item.children ?? [], contentLayout, typography, itemCtx, {}, item),
        intrinsicWidthTwips,
        contentHeightTwips,
        minHeightTwips: itemCss.minHeightTwips,
        stackedContent: true,
      };
    }

    return {
      layout: itemLayout,
      intrinsicWidthTwips,
      minHeightTwips: itemCss.minHeightTwips,
      blocks: [
        makeParagraph(
          collectInlineRunsFromNodes(item.children ?? [], typography, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions),
          { alignment: itemLayout.alignment },
          { flexCompact: true },
        ),
      ],
    };
  });

  // A flex container with no visible items renders nothing — don't emit an empty
  // table, and above all don't paint its background as a filled block. Real docs
  // pages wrap figures in a `display:flex` "open in modal" control whose actual
  // content is hidden/lazy in the export; all that survives is the container's
  // background-color, which otherwise becomes a bare colored box (the "blue box").
  if (items.length === 0) return [];

  // Likewise, a flex container whose items carry NO visible content — no text, no
  // media, no fill, no border on container or items — emits a bare empty table
  // (icon-only web-component chrome, e.g. a table widget's expand control whose glyph
  // is in shadow DOM). Beyond being noise, an empty sliver table is destructive: docx
  // merges adjacent sibling tables, so a 600-twip empty table emitted right after a
  // real data table fuses with it and collapses the data table to sliver width.
  const containerVisible =
    Boolean(containerLayout.shading?.fill) || Boolean(containerLayout.borders);
  const hasVisibleContent =
    containerVisible ||
    flexItemElements(element, ctx.styleResolver).some((item) => {
      if (elementPlainText(item).length > 0) return true;
      if (flexItemHasRasterMedia(item)) return true;
      if (item.name.toLowerCase() === "svg" || $(item).find("svg").length > 0) return true;
      const itemCss = cssToBlockLayout(ctx.styleResolver.getCss(item));
      return Boolean(itemCss.shading?.fill) || Boolean(itemCss.borders);
    });
  if (!hasVisibleContent) return [];

  const table =
    flex.direction === "column"
      ? makeFlexColumnTable(items, flex.gap, containerLayout)
      : makeFlexRowTable(items, flex.gap, containerLayout);

  const wrapped: DocxBlock[] = [];
  const topSpacer = marginSpacer(containerLayout.marginTop);
  if (topSpacer) wrapped.push(topSpacer);
  wrapped.push(table);
  const bottomSpacer = marginSpacer(containerLayout.marginBottom);
  if (bottomSpacer) wrapped.push(bottomSpacer);
  return wrapped;
}

function processBlockContainer(
  $: CheerioAPI,
  element: Element,
  ctx: VisitorContext,
  extra: Record<string, unknown> = {},
): DocxBlock[] {
  if (isFlexContainer(element, ctx.styleResolver)) {
    return processFlexContainer($, element, ctx);
  }

  const layout = resolveBlockLayout(ctx, element);
  const typography = typographyFromBlockElement(element, ctx.styleResolver);
  // text-align inherits (CSS): a container's alignment flows to block children
  // (`<figure>` captions, `<center>`, `<div align=center>` wrappers).
  const inheritAlignment = layout.alignment ? { alignment: layout.alignment } : undefined;
  const childCtx: VisitorContext = { ...ctx, inheritedLayout: inheritAlignment };

  if (hasDirectBlockChild(element, ctx.styleResolver)) {
    const children = getDirectBlockChildren(element, ctx.styleResolver);

    if (layout.shading?.fill) {
      const blocks: DocxBlock[] = [];
      const topSpacer = marginSpacer(layout.marginTop);
      if (topSpacer) blocks.push(topSpacer);

      const childBlocks: DocxBlock[] = [];
      for (const child of children) {
        childBlocks.push(...visitElement($, child, childCtx));
      }
      // Pass the container's own borders (e.g. a callout's border-left) — the
      // cell paints fill AND bar together.
      blocks.push(makeShadedContainerTable(childBlocks, layout, layout.borders));

      const bottomSpacer = marginSpacer(layout.marginBottom);
      if (bottomSpacer) blocks.push(bottomSpacer);
      return blocks;
    }

    // Walk children in order so inline content between block children (e.g. an
    // `<img>` inside a `<figure>` alongside its `<figcaption>`) isn't dropped.
    const blocks: DocxBlock[] = [];
    let pendingInline: AnyNode[] = [];
    let forceBreakBefore = Boolean(layout.pageBreakBefore);
    const flushInline = (): void => {
      if (pendingInline.length === 0) return;
      const runs = collectInlineRunsFromNodes(pendingInline, typography, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions);
      if (runs.length > 0) {
        // First emitted block carries the container's top margin (e.g. `<figure margin:8px>`).
        const isFirst = blocks.length === 0;
        const flushLayout: BlockLayout = {
          alignment: layout.alignment,
          spacingBefore: isFirst ? layout.marginTop : undefined,
          pageBreakBefore: isFirst && forceBreakBefore ? true : undefined,
        };
        blocks.push(...emitBlockContent(runs, flushLayout, {}, element));
        if (isFirst) forceBreakBefore = false;
      }
      pendingInline = [];
    };
    for (const child of element.children ?? []) {
      if (isElement(child) && isBlockElement(child, ctx.styleResolver)) {
        flushInline();
        const visitCtx = forceBreakBefore ? { ...childCtx, pageBreakBeforeNext: true } : childCtx;
        blocks.push(...visitElement($, child, visitCtx));
        forceBreakBefore = elementRequestsPageBreakAfter(child, ctx.styleResolver);
      } else {
        pendingInline.push(child);
      }
    }
    flushInline();
    return blocks;
  }

  return emitFlowBlocks($, element.children ?? [], layout, typography, ctx, extra, element);
}

function listContainerMargins(
  listElement: Element,
  styleResolver: StyleResolver,
): { marginTop?: number; marginBottom?: number } {
  const css = styleResolver.getCss(listElement);
  if (styleResolver.source === "computed") {
    return { marginTop: css.marginTop, marginBottom: css.marginBottom };
  }
  // UA: nested lists (`ul ul`, `ol ul`, …) have zero vertical margins.
  const parent = listElement.parent;
  if (parent && isElement(parent)) {
    const parentTag = parent.name.toLowerCase();
    if (parentTag === "li" || parentTag === "ul" || parentTag === "ol") {
      return { marginTop: css.marginTop, marginBottom: css.marginBottom };
    }
  }
  const defaultMargin = pxToTwips(DEFAULT_PARAGRAPH_MARGIN_PX);
  let marginTop: number | undefined = css.marginTop ?? defaultMargin;
  const prev = prevElementSibling(listElement);
  if (prev && isElement(prev) && marginTop) {
    const prevTag = prev.name.toLowerCase();
    if (prevTag === "p" || /^h[1-6]$/.test(prevTag) || prevTag === "blockquote") {
      marginTop = undefined;
    }
  }
  return {
    marginTop,
    marginBottom: css.marginBottom ?? defaultMargin,
  };
}

/** `<ol type="a">` / `<ul type="square">` → CSS list-style-type keyword. */
const LIST_TYPE_ATTR: Record<string, string> = {
  "1": "decimal",
  a: "lower-alpha",
  A: "upper-alpha",
  i: "lower-roman",
  I: "upper-roman",
  disc: "disc",
  circle: "circle",
  square: "square",
};

function resolveListReference(
  listElement: Element,
  tag: string,
  styleResolver: StyleResolver,
): string {
  const styleType =
    styleResolver.getCss(listElement).listStyleType ??
    (listElement.attribs?.type ? LIST_TYPE_ATTR[listElement.attribs.type] : undefined);
  if (styleType && LIST_STYLE_REFERENCES[styleType]) return LIST_STYLE_REFERENCES[styleType];
  return tag === "ol" ? "numbers" : "bullets";
}

/** Exported for table cells — lists inside `<td>` use the same numbering path. */
export function processList(
  $: CheerioAPI,
  listElement: Element,
  ctx: VisitorContext,
): DocxBlock[] {
  const tag = listElement.name.toLowerCase();
  const listRef = resolveListReference(listElement, tag, ctx.styleResolver);
  const blocks: DocxBlock[] = [];
  const { marginTop, marginBottom } = listContainerMargins(listElement, ctx.styleResolver);
  const topSpacer = marginSpacer(
    marginTop,
    ctx.blockquoteDepth > 0 ? ctx.inheritedLayout : undefined,
  );
  if (topSpacer) blocks.push(topSpacer);

  $(listElement)
    .children("li")
    .each((_, li) => {
      blocks.push(...processListItem($, li, { ...ctx, listRef, listLevel: ctx.listLevel }));
    });

  const bottomSpacer = marginSpacer(marginBottom);
  if (bottomSpacer) blocks.push(bottomSpacer);
  return blocks;
}

function listItemParagraphExtra(
  ctx: VisitorContext,
): Record<string, unknown> {
  return {
    style: ctx.listRef?.startsWith("numbers") ? "ListNumber" : "ListBullet",
    numbering: { reference: ctx.listRef ?? "bullets", level: ctx.listLevel },
  };
}

/** OOXML caps paragraph border `w:space` at 31pt. */
const MAX_BORDER_SPACE_PT = 31;

/**
 * A direct `w:ind` on a numbered paragraph overrides the numbering style's indent.
 * When a list item inherits an indent (blockquote), stack the list level indent on
 * top of it, restore the hanging marker column, and pin the tab stop at the text
 * start. The inherited quote bar is re-aimed at the quote content edge (clamped).
 */
function listItemLayout(layout: BlockLayout, ctx: VisitorContext): {
  layout: BlockLayout;
  extra: Record<string, unknown>;
} {
  const extra = listItemParagraphExtra(ctx);
  if (!layout.indentLeft) return { layout, extra };

  const left = layout.indentLeft + LIST_LEVEL_LEFT_TWIPS * (ctx.listLevel + 1);
  const itemLayout: BlockLayout = {
    ...layout,
    indentLeft: left,
    hangingIndent: LIST_HANGING_TWIPS,
  };
  const quoteBar = layout.borders?.left;
  if (quoteBar) {
    // The border anchors at the paragraph's leftmost extent (left − hanging).
    // Aim the bar back at the quote content edge; if the OOXML space cap (31pt)
    // would strand it far from the browser's bar, omit it instead.
    const space =
      Math.round((left - LIST_HANGING_TWIPS - layout.indentLeft) / 20) +
      quoteBar.space;
    if (space <= MAX_BORDER_SPACE_PT) {
      itemLayout.borders = { ...layout.borders, left: { ...quoteBar, space } };
    } else {
      const { left: _left, ...rest } = layout.borders ?? {};
      itemLayout.borders = Object.keys(rest).length > 0 ? rest : undefined;
    }
  }
  extra.tabStops = [{ type: TabStopType.LEFT, position: left }];
  return { layout: itemLayout, extra };
}

function processListItem(
  $: CheerioAPI,
  li: Element,
  ctx: VisitorContext,
): DocxBlock[] {
  const blocks: DocxBlock[] = [];
  const inlineNodes = getInlineOrTextNodes(li, ctx.styleResolver);
  const resolved = resolveBlockLayout(ctx, li);
  const { layout, extra } = listItemLayout(resolved, ctx);
  const typography = typographyFromBlockElement(li, ctx.styleResolver);

  if (inlineNodes.length > 0) {
    blocks.push(
      ...emitBlockContent(
        collectInlineRunsFromNodes(inlineNodes, typography, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions),
        layout,
        extra,
      ),
    );
  }

  for (const child of li.children ?? []) {
    if (!isElement(child)) continue;
    const tag = child.name.toLowerCase();
    if (tag === "ul" || tag === "ol") {
      blocks.push(...processList($, child, { ...ctx, listLevel: ctx.listLevel + 1 }));
    } else if (isBlockElement(child, ctx.styleResolver)) {
      blocks.push(...visitElement($, child, ctx));
    }
  }

  if (blocks.length === 0) {
    blocks.push(...emitBlockContent([], layout, extra));
  }

  return blocks;
}

function applyDefaultHeadingMargins(
  element: Element,
  layout: BlockLayout,
  fontSizeHalfPoints: number,
  styleResolver: StyleResolver,
): BlockLayout {
  if (styleResolver.source === "computed") return layout;
  const inlineStyle = element.attribs?.style ?? "";
  if (/\bmargin(?:\s|:|-)/i.test(inlineStyle)) return layout;

  const tag = element.name.toLowerCase() as keyof typeof HEADING_MARGIN_EM;
  const factor = HEADING_MARGIN_EM[tag] ?? 1;
  const fontPx = fontSizeHalfPoints / 1.5;
  const margin = pxToTwips(fontPx * factor);

  return {
    ...layout,
    marginTop: layout.marginTop ?? margin,
    marginBottom: layout.marginBottom ?? margin,
  };
}

function processHeading($: CheerioAPI, element: Element, ctx: VisitorContext): DocxBlock[] {
  let layout = resolveBlockLayout(ctx, element);
  const tag = element.name.toLowerCase() as keyof typeof HEADING_LEVELS;
  const fromElement = typographyFromBlockElement(element, ctx.styleResolver);
  const fontSize = fromElement.fontSize ?? HEADING_FONT_HALF_POINTS[tag];
  layout = applyDefaultHeadingMargins(element, layout, fontSize, ctx.styleResolver);
  layout = {
    ...layout,
    spacingBefore: sumTwips(layout.paddingTop, layout.marginTop),
    spacingAfter: sumTwips(layout.paddingBottom, layout.marginBottom),
  };
  // A heading should never smash into the block above it. Web layouts routinely
  // zero heading margins and rely on flex/grid `gap` or container padding for the
  // spacing — which has no equivalent in a flat docx (the computed path faithfully
  // reproduces `margin-top: 0`). Floor the top spacing to ~0.5em of the heading
  // font so a heading always keeps breathing room; a larger real margin still wins.
  // Skipped inside flex cards, which manage their own tight vertical rhythm.
  if (!ctx.flexBlockContent) {
    const headingTopFloor = pxToTwips((fontSize / 1.5) * 0.5);
    layout = { ...layout, spacingBefore: Math.max(layout.spacingBefore ?? 0, headingTopFloor) };
  }
  const typography: RunTypography = {
    ...fromElement,
    bold: true,
    fontSize,
    // Word Heading 1–6 styles default to theme blue; inherit body foreground when
    // CSS does not set a color (browser inherits body `color`, inline path never sees it).
    color: fromElement.color ?? BODY_TEXT_COLOR,
  };
  const hasBlockShading = Boolean(layout.shading?.fill);
  const fontSizePx = fontSize / 1.5;
  const headingLayout: BlockLayout = hasBlockShading
    ? {
        ...layout,
        shadedContentLinePx: fontSizePx * 1.4,
        shadedTabHalfPoints: fontSize,
      }
    : layout;

  return emitBlockContent(
    collectInlineRunsFromNodes(element.children ?? [], typography, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions),
    headingLayout,
    hasBlockShading ? {} : { heading: HEADING_LEVELS[tag], atLeastLineTwips: atLeastLineTwips(fontSize, layout.lineHeight) },
    element,
  );
}

function processBlockquote($: CheerioAPI, element: Element, ctx: VisitorContext): DocxBlock[] {
  const depth = ctx.blockquoteDepth + 1;
  const parentIndent =
    ctx.blockquoteDepth > 0 ? (ctx.inheritedLayout?.indentLeft ?? 0) : 0;
  const quoteLayout = blockquoteLayout(depth, element, ctx.styleResolver, parentIndent);
  const childCtx: VisitorContext = {
    ...ctx,
    blockquoteDepth: depth,
    inheritedLayout: quoteLayout,
  };

  let blocks: DocxBlock[];
  if (hasDirectBlockChild(element) && quoteLayout.shading?.fill) {
    // Shaded blockquote with block children: one container cell paints fill +
    // bar + padding together (shading is not inheritable, so per-child
    // inheritance would render the bar but lose the background). Children must
    // NOT also inherit the bar/indent — the cell handles both.
    const shadedCtx: VisitorContext = { ...childCtx, inheritedLayout: undefined };
    const childBlocks = visitNodes($, element.children ?? [], shadedCtx);
    blocks = [makeShadedContainerTable(childBlocks, quoteLayout, quoteLayout.borders)];
  } else if (hasDirectBlockChild(element)) {
    blocks = visitNodes($, element.children ?? [], childCtx);
  } else {
    const typography = typographyFromBlockElement(element, ctx.styleResolver);
    blocks = emitBlockContent(
      collectInlineRunsFromNodes(element.children ?? [], typography, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions),
      quoteLayout,
      {},
      element,
    );
  }

  const wrapped: DocxBlock[] = [];
  const topSpacer = marginSpacer(
    quoteLayout.marginTop,
    ctx.blockquoteDepth > 0 ? ctx.inheritedLayout : undefined,
  );
  if (topSpacer) wrapped.push(topSpacer);
  wrapped.push(...blocks);
  const bottomSpacer = marginSpacer(quoteLayout.marginBottom);
  if (bottomSpacer) wrapped.push(bottomSpacer);
  return wrapped;
}

/** Chromium default monospace font size (`<pre>`/`<code>` with no explicit size). */
const MONOSPACE_DEFAULT_PX = 13;

/** Text content with literal newlines preserved (`white-space: pre`); `<br>` → newline. */
function rawPreText(element: Element): string {
  const parts: string[] = [];
  const walk = (nodes: AnyNode[]): void => {
    for (const node of nodes) {
      if (node.type === "text") parts.push(node.data ?? "");
      else if (isElement(node)) {
        if (node.name.toLowerCase() === "br") parts.push("\n");
        else walk(node.children ?? []);
      }
    }
  };
  walk(element.children ?? []);
  return parts.join("").replace(/^\n+/, "").replace(/\s+$/, "");
}

/**
 * `<pre>` → one run per line with explicit breaks (inline collection would
 * collapse the newlines), sized per the element's font (UA monospace 13px).
 */
function processPreBlock(element: Element, ctx: VisitorContext): DocxBlock[] {
  const css = ctx.styleResolver.getCss(element);
  const layout = resolveBlockLayout(ctx, element);
  const fontSize = css.fontSize ?? pxToHalfPoints(MONOSPACE_DEFAULT_PX);
  // Own font-family only — UA `pre { font-family: monospace }` beats inheritance.
  const font = css.fontFamily ?? "Consolas";
  const runs = rawPreText(element)
    .split("\n")
    .map(
      (line, index) =>
        new TextRun({
          text: line,
          font,
          size: fontSize,
          ...(index > 0 ? { break: 1 } : {}),
        }),
    );
  const preLayout: BlockLayout = {
    ...layout,
    // EXACT per-line box matching the CSS line height of the pre's own font.
    shadedContentLinePx: (fontSize / 1.5) * 1.4,
  };
  return emitBlockContent(runs, preLayout, {}, element);
}

/** Chromium `<hr>`: 1px inset border paints ~2px gray; margin 0.5em collapses with neighbors. */
const HR_DEFAULT_COLOR = "999999";
const HR_DEFAULT_SIZE_EIGHTHS = 8;
const HR_MARGIN_PX = 7;
/** EXACT line height (twips) placing the bottom border at the browser's rule y. */
const HR_LINE_TWIPS = 65;

/**
 * `<hr>` → minimal-height paragraph with a bottom border (the Word idiom for a
 * rule). The EXACT spacer line keeps the border at the browser's y-position
 * instead of hanging below an empty full-height line box.
 */
function processHorizontalRule(element: Element, ctx: VisitorContext): Paragraph {
  const css = ctx.styleResolver.getCss(element);
  const side = css.borderTop ?? css.borderBottom ?? css.border;
  const color = side?.color ?? css.backgroundColor ?? HR_DEFAULT_COLOR;
  const size = side
    ? Math.max(4, Math.round(side.widthPx * 6))
    : HR_DEFAULT_SIZE_EIGHTHS;

  // Adjacent paragraphs/headings/blockquotes already emit collapsed margins
  // (which exceed the hr's own 0.5em); only provide a gap when they don't.
  const prev = prevElementSibling(element);
  const prevTag = prev?.name.toLowerCase() ?? "";
  const prevProvidesGap =
    prevTag === "p" || /^h[1-6]$/.test(prevTag) || prevTag === "blockquote";
  const before = css.marginTop ?? (prevProvidesGap ? 0 : pxToTwips(HR_MARGIN_PX));
  const after = css.marginBottom ?? 0;

  return new Paragraph({
    spacing: {
      before,
      after,
      line: HR_LINE_TWIPS,
      lineRule: LineRuleType.EXACT,
    },
    border: {
      bottom: { style: BorderStyle.SINGLE, size, space: 0, color },
    },
    children: [emptyRun()],
  });
}

export function visitElement(
  $: CheerioAPI,
  element: Element,
  ctx: VisitorContext = DEFAULT_VISITOR_CONTEXT,
): DocxBlock[] {
  if (isHiddenElement(element, ctx.styleResolver)) return [];
  const tag = element.name.toLowerCase();

  let blocks: DocxBlock[];
  switch (tag) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      blocks = processHeading($, element, ctx);
      break;
    case "p":
    case "div":
    case "section":
      blocks = processBlockContainer($, element, ctx);
      break;
    case "blockquote":
      blocks = processBlockquote($, element, ctx);
      break;
    case "ul":
    case "ol":
      blocks = processList($, element, ctx);
      break;
    case "table": {
      // CSS margins on the table element → spacer paragraphs (also keeps
      // adjacent tables from merging into one in Word).
      const tableCss = ctx.styleResolver.getCss(element);
      const tableBlocks: DocxBlock[] = [];
      const topSpacer = marginSpacer(tableCss.marginTop);
      if (topSpacer) tableBlocks.push(topSpacer);
      // `<caption>` renders above the table, centered (UA default).
      const caption = $(element).children("caption").first().toArray()[0];
      if (caption) {
        tableBlocks.push(
          makeParagraph(
            collectInlineRunsFromNodes(
              caption.children ?? [],
              typographyFromBlockElement(caption, ctx.styleResolver),
              undefined,
              ctx.styleResolver,
              ctx.defaultSizeHalfPoints,
              ctx.fieldOptions,
            ),
            { alignment: AlignmentType.CENTER },
          ),
        );
      }
      tableBlocks.push(convertTable($, element, ctx.styleResolver));
      const bottomSpacer = marginSpacer(tableCss.marginBottom);
      if (bottomSpacer) tableBlocks.push(bottomSpacer);
      blocks = tableBlocks;
      break;
    }
    case "svg":
      blocks = convertSvg(element);
      break;
    case "pre":
      blocks = processPreBlock(element, ctx);
      break;
    case "hr":
      blocks = [processHorizontalRule(element, ctx)];
      break;
    default:
      blocks = processBlockContainer($, element, ctx);
      break;
  }

  // `id` on any block element → Word bookmark so `href="#id"` can jump here.
  return prependElementBookmark(element.attribs?.id, blocks);
}

export function visitNodes(
  $: CheerioAPI,
  nodes: AnyNode[],
  ctx: VisitorContext = DEFAULT_VISITOR_CONTEXT,
): DocxBlock[] {
  const blocks: DocxBlock[] = [];
  let pendingInline: AnyNode[] = [];
  let breakBeforeNext = Boolean(ctx.pageBreakBeforeNext);

  const flushInline = (): void => {
    if (pendingInline.length === 0) return;
    blocks.push(
      makeParagraph(
        collectInlineRunsFromNodes(pendingInline, {}, undefined, ctx.styleResolver, ctx.defaultSizeHalfPoints, ctx.fieldOptions),
        {
          ...(ctx.inheritedLayout ?? {}),
          ...(breakBeforeNext ? { pageBreakBefore: true } : {}),
        },
      ),
    );
    breakBeforeNext = false;
    pendingInline = [];
  };

  for (const node of nodes) {
    if (node.type === "text") {
      if ((node.data ?? "").trim()) pendingInline.push(node);
      continue;
    }

    if (!isElement(node)) continue;

    if (isBlockElement(node, ctx.styleResolver)) {
      flushInline();
      const childCtx: VisitorContext = {
        ...(ctx.blockquoteDepth > 0 || ctx.inheritedLayout
          ? ctx
          : { ...ctx, inheritedLayout: undefined }),
        pageBreakBeforeNext: breakBeforeNext || undefined,
      };
      blocks.push(...visitElement($, node, childCtx));
      breakBeforeNext = elementRequestsPageBreakAfter(node, ctx.styleResolver);
    } else {
      pendingInline.push(node);
    }
  }

  flushInline();
  return blocks;
}

/**
 * docx merges adjacent sibling tables into ONE table (Word/LibreOffice both do this),
 * which destroys layout: a narrow table emitted right after a wide data table fuses
 * with it and collapses the data table to the narrow grid. Insert an invisible
 * 1-twip-line separator paragraph between consecutive tables so each keeps its own
 * grid. (Tables separated by real content or margin spacers are already safe.)
 */
function separateAdjacentTables(blocks: FileChild[]): FileChild[] {
  const out: FileChild[] = [];
  for (const block of blocks) {
    if (out.length > 0 && out[out.length - 1] instanceof Table && block instanceof Table) {
      out.push(
        new Paragraph({
          spacing: { before: 0, after: 0, line: 1, lineRule: LineRuleType.EXACT },
          children: [],
        }),
      );
    }
    out.push(block);
  }
  return out;
}

export function htmlToDocxBlocks(
  $: CheerioAPI,
  styleResolver: StyleResolver = INLINE_STYLE_RESOLVER,
  defaultSizeHalfPoints: number = DEFAULT_VISITOR_CONTEXT.defaultSizeHalfPoints,
  fieldOptions?: InlineFieldOptions,
): FileChild[] {
  const ctx: VisitorContext = { ...DEFAULT_VISITOR_CONTEXT, styleResolver, defaultSizeHalfPoints, fieldOptions };
  const nodes = $("body").contents().toArray();
  const blocks = separateAdjacentTables(visitNodes($, nodes, ctx));

  if (blocks.length === 0) {
    return [new Paragraph({ children: [new TextRun("")] })];
  }
  return blocks;
}
